import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon, DocumentTextIcon, CurrencyDollarIcon, TruckIcon, ScaleIcon, CreditCardIcon, CalculatorIcon, ArrowPathIcon, InformationCircleIcon, XMarkIcon, PencilSquareIcon, MagnifyingGlassIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

import { POSidebar } from '../../components/POSidebar';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPO, getStatsForFinance, PurchaseOrdersLoadingKeys, syncPOItem, syncPOToQB, upsertInvoicesBatch } from '../../slices/purchasesOrdersSlice';
import { CANCELLED, DRAFT, excludedStatusesForEmail, statuses } from '../../consts/status';
import { useDebounce } from '../../helpers/useDebounce';
import { arrayHasItems, formatPrice, getPastDueDate, getTotalOwedTextColor, scrollToTop } from '../../helpers/common';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { OverlayGrayBackground } from '../../components/OverlayGrayBackground';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../consts/general';
import { FinanceStatusFilter } from '../../components/finance/FinanceStatusFilter';
import { MobileFilterSheet } from '../../components/finance/MobileFilterSheet';
import { getCronFinanceStatus, getCronQuickBooksStatus } from '../../slices/cronSlice';
import { FINANCE_TYPE, QUICKBOOKS_TYPE } from '../../consts/cronTypes';
import { CronUpdatesBlock } from '../../components/CronUpdatesBlock';
import { SHIPPING } from '../../consts/paymentTypes';
import axios from 'axios';
import { QuickBooksUpdatesBlock } from '../../components/QuickBooksUpdatesBlock';
import { fetchQuickBooksRefreshToken, getRefreshTokenData, QBLoadingKeys, refreshMyAccessToken } from '../../slices/quickBooksSlice';
import { ACCESS, REFRESH } from '../../consts/quickBooksTypes';
import { NotificationIcon } from '../../components/quickbooks/NotificationIcon';
import { NotificationModal } from '../../components/quickbooks/NotificationModal';
import { ReportModal } from '../../components/quickbooks/ReportModal';
import ReactTooltip from 'react-tooltip';
import { financePagePOTooltip } from '../../helpers/tooltip';
import { redirectToTheQuickBooksAuth } from '../../helpers/quickbooks';
import { usePageReset } from '../../helpers/usePageReset';
import { MobilePOCard } from '../../components/finance/MobilePOCard';
import { BulkBar } from '../../components/finance/BulkBar';
import { BulkPaymentDialog } from '../../components/finance/BulkPaymentDialog';
import { BulkEditPaymentsDialog } from '../../components/finance/BulkEditPaymentsDialog';
import { BulkInvoiceDialog } from '../../components/finance/BulkInvoiceDialog';
import { MilestoneChips } from '../../components/finance/MilestoneChips';
import { createPaymentsBatch, updatePaymentsBatch } from '../../slices/paymentsSlice';
import PurchaseOrderStatusChip from '../purchaseOrders/PurchaseOrderStatusChip';

const financeStatusOptions = statuses.filter((status) => status !== DRAFT);

const Finance = ({
  testId,
  access,
  testData
}) => {
  const navigate = useNavigate();
  const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
  const [authCode] = useState(pageParams?.get('code') || "");
  const dispatch = useDispatch();
  const wrapperTableRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(pageParams?.get('search') || '');
  const [selectedPO, setSelectedPO] = useState(null);
  const [currentPage, setCurrentPage] = useState(Number(pageParams?.get('page') || 1));
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(Number(pageParams?.get('itemsPerPage') || 50));
  const [filters, setFilters] = useState({
    status: pageParams?.get('statuses')?.split(',').filter((status) => status && status !== DRAFT) || []
  });

  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkInvoiceDialogOpen, setBulkInvoiceDialogOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [openedReportModal, setOpenedReportModal] = useState(false);
  const [openedNotificationModal, setOpenedNotificationModal] = useState({
    opened: false,
    error: null
  });

  const currentSearchValue = useDebounce(searchTerm, 800);
  
  // Auto-reset page to 1 when non-page params change
  const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, 'get:/api/v1/purchase-orders');
  
  const {
    purchaseOrders,
    stats,
    pagination,
    syncingPOs,
    syncingQBPOs,
    loadingKeys: loadingKeysPO,
    loading: loadingPO,
    isFirstLoadComplete
  } = useSelector((state) => state.purchasesOrders);
  const { currentUser } = useSelector((state) => state.auth);
  const [orderedPurchases, setOrderedPurchases] = useState(
    testId && testData?.orders !== undefined ? testData?.orders : (purchaseOrders || [])
  );
  const {
    loading: cronLoading,
    data: cronData
  } = useSelector((state) => state.cron);
  const {
    loading: quickBooksLoading,
    firstLoading: quickBooksFirstLoading,
    loadingKeys: qbLoadingKeys,
    reportData,
    data: quickBooksData
  } = useSelector((state) => state.quickBooks);

  const currentStats = testId ? (testData?.stats || {}) : stats;
  const currentFirstLoadComplete = testId && (testData?.pageIsLoaded !== undefined) ? testData?.pageIsLoaded : isFirstLoadComplete;
  let counter = 1;
  useEffect(() => {
    if (testId) return
    dispatch(getStatsForFinance({}))
    dispatch(getCronFinanceStatus({ type: FINANCE_TYPE }))
    // dispatch(getCronQuickBooksStatus({ type: QUICKBOOKS_TYPE }))
  }, [])

  useEffect(() => {
    dispatch(getRefreshTokenData({}))
  }, []);

  useEffect(() => {
    if (testId) return
    scrollToTop();
  }, [currentPage, currentItemsPerPage]);

  useEffect(() => {
    if (authCode && counter === 1) {
      counter += 1;
      dispatch(fetchQuickBooksRefreshToken({ code: authCode }))
    }
  }, [authCode, counter]);

  useEffect(() => {
    if (testId) return
    const currentPurchases = arrayHasItems(purchaseOrders) ? purchaseOrders : [];
    if (selectedPO) {
      setSelectedPO(currentPurchases.find((purchase) => purchase.id === selectedPO.id))
    };
    setOrderedPurchases(currentPurchases);
  }, [purchaseOrders])

  useEffect(() => {
    if (testId) return
    
    if (currentPage === 1 && shouldSkipRequest()) {
      const params = new URLSearchParams({
        search: currentSearchValue,
        statuses: filters.status,
        page: currentPage,
        itemsPerPage: currentItemsPerPage
      });
      navigate(`${ROUTES.FINANCE}?${params}`, { replace: true });
      return;
    }
    
    dispatch(getAllPO({
      search: currentSearchValue,
      statuses: filters.status,
      page: currentPage,
      itemsPerPage: currentItemsPerPage
    }));
    const params = new URLSearchParams({
      search: currentSearchValue,
      statuses: filters.status,
      page: currentPage,
      itemsPerPage: currentItemsPerPage
    });
    navigate(`${ROUTES.FINANCE}?${params}`, { replace: true })
  }, [
    currentSearchValue,
    filters.status,
    currentPage,
    currentItemsPerPage
  ])

  const handleFilterChange = (filterType, selectedOptions) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: selectedOptions
    }));
  };
  const pastDueDatesBlockIsVisible = (status) => {
    return !excludedStatusesForEmail.includes(status);
  }
  const selectedPOs = orderedPurchases.filter((po) => selectedPOIds.includes(po.id));
  const selectedOwedTotal = selectedPOs.reduce((acc, po) => acc + Number(po.amountOwed || 0), 0);

  const toggleSelectPO = (poId) => {
    setSelectedPOIds((prev) => (
      prev.includes(poId) ? prev.filter((id) => id !== poId) : [...prev, poId]
    ));
  };

  const toggleSelectAll = () => {
    const visibleIds = orderedPurchases.map((po) => po.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedPOIds.includes(id));
    setSelectedPOIds(allSelected ? [] : visibleIds);
  };

  const handleBulkSubmit = (rows) => {
    dispatch(createPaymentsBatch({
      payments: rows.map((row) => ({
        poId: row.poId,
        payment: {
          type: row.type,
          transaction_id: row.transactionId || null,
          note: row.note || null,
          date_paid: row.date,
          amount: row.amount,
          po_id: row.poId
        }
      })),
      search: currentSearchValue,
      statuses: filters.status,
      page: currentPage,
      itemsPerPage: currentItemsPerPage
    }));
    setBulkDialogOpen(false);
    setSelectedPOIds([]);
  };

  const handleBulkEditSubmit = (rows) => {
    dispatch(updatePaymentsBatch({
      payments: rows.map((row) => ({
        poId: row.poId,
        payment: {
          id: row.paymentId,
          type: row.type,
          transaction_id: row.transactionId || null,
          note: row.note || null,
          date_paid: row.date,
          amount: row.amount,
          po_id: row.poId
        }
      })),
      search: currentSearchValue,
      statuses: filters.status,
      page: currentPage,
      itemsPerPage: currentItemsPerPage
    }));
    setBulkEditDialogOpen(false);
    setSelectedPOIds([]);
  };

  const handleBulkInvoiceSubmit = (invoices) => {
    dispatch(upsertInvoicesBatch({
      invoices,
      search: currentSearchValue,
      statuses: filters.status,
      page: currentPage,
      itemsPerPage: currentItemsPerPage
    }));
    setBulkInvoiceDialogOpen(false);
    setSelectedPOIds([]);
  };

  const bulkSyncing = selectedPOs.some((po) => syncingPOs.includes(po.id));

  const handleBulkSync = () => {
    selectedPOs.forEach((po) => {
      if (po.id && !syncingPOs.includes(po.id)) {
        const shippingPriceOfThePO = (po.payments || [])
          .filter((payment) => payment.type === SHIPPING)
          .reduce((acc, payment) => acc + Number(payment.amount || 0), 0);
        dispatch(syncPOItem({
          poId: po.id,
          shippingPrice: String(shippingPriceOfThePO),
          search: currentSearchValue,
          statuses: filters.status,
          page: currentPage,
          itemsPerPage: currentItemsPerPage
        }));
      }
    });
  };

  const statsIsLoading = () => testId && testData?.statsLoading ? true : loadingKeysPO.includes(PurchaseOrdersLoadingKeys.stats);
  const financeTableIsLoading = () => loadingPO && loadingKeysPO.includes(PurchaseOrdersLoadingKeys.orders);
  const qbTokenBlockIsLoading = () => quickBooksLoading && qbLoadingKeys.includes(QBLoadingKeys.refreshToken);
  const reportDataIsLoading = () => quickBooksLoading && qbLoadingKeys.includes(QBLoadingKeys.report);

  return (
    <main className="w-full h-full px-4 sm:px-6 lg:px-8" data-testid={testId}>
      <div className="h-full py-6 flex flex-col">
        <div className="flex flex-wrap gap-2 mb-4 items-center justify-between md:gap-4">
          <h1 className="text-xl font-bold tracking-tight text-gray-900">PO Accounts Payable</h1>
          <div className="flex flex-wrap items-center gap-2">
            {access === "edit" && (
              <QuickBooksUpdatesBlock
                redirectRequestFunction={() => redirectToTheQuickBooksAuth()}
                refreshTokenFunction={() => dispatch(refreshMyAccessToken({}))}
                expiredAccessTokenDate={quickBooksData[ACCESS]?.expired_date}
                errorTextAccessToken={quickBooksData[ACCESS]?.error}
                expiredRefreshTokenDate={quickBooksData[REFRESH]?.expired_date}
                errorTextRefreshToken={quickBooksData[REFRESH]?.error}
                isLoading={qbTokenBlockIsLoading()}
                isFirstLoading={quickBooksFirstLoading}
              />
            )}
            <CronUpdatesBlock
              type={FINANCE_TYPE}
              status={cronData[FINANCE_TYPE]?.process_status}
              updatedDate={cronData[FINANCE_TYPE]?.last_update}
              errorText={cronData[FINANCE_TYPE]?.process_error}
              isLoading={cronLoading}
            />
            {/* <CronUpdatesBlock
              type={QUICKBOOKS_TYPE}
              status={cronData[QUICKBOOKS_TYPE]?.process_status}
              updatedDate={cronData[QUICKBOOKS_TYPE]?.last_update}
              errorText={cronData[QUICKBOOKS_TYPE]?.process_error}
              isLoading={cronLoading}
            /> */}
          </div>
        </div>
        {/* Summary stats section remains unchanged */}
        {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Deposit Owed', key: "depositOwed", color: 'text-blue-600', colorSpinner: 'blue', icon: CurrencyDollarIcon },
            { label: 'Shipping Owed', key: "shippingOwed", color: 'text-green-600', colorSpinner: 'green', icon: TruckIcon },
            { label: 'Balance Owed', key: "balanceOwed", color: 'text-yellow-600', colorSpinner: 'yellow', icon: ScaleIcon },
            { label: 'Credit Owed', key: "creditOwed", color: 'text-purple-600', colorSpinner: 'purple', icon: CreditCardIcon },
            { label: 'Total Owed', key: "totalOwed", color: 'text-red-600', colorSpinner: 'red', icon: CalculatorIcon },
          ].map((stat, index) => (
            <div key={`${stat.key}_${index}`} className="bg-white p-4 rounded-lg shadow min-h-28">
              <div className="flex items-center justify-between mb-2 gap-2">
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
                <p className="text-sm font-medium text-gray-500">{stat.label}</p>
              </div>
              {statsIsLoading() ? (
                <LoaderSpinner border='3' width='32' height='32' parentHeight='h-auto' colorSpinner={stat.colorSpinner} testId={testId ? `${testId}_spinner_stat_${index}` : undefined} />
              ) : (
                <p className={`text-2xl font-bold ${stat.color}`} data-testid={testId ? `${testId}_stats_${index}` : undefined}>
                  {Object.keys(currentStats).length > 0 && `$${currentStats[stat.key].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </p>
              )}
            </div>
          ))}
        </div> */}

        {/* PO Table and Sidebar Container */}
        <div className="flex bg-gray-100 sm:rounded-lg flex-grow relative h-full">
          {financeTableIsLoading() && <OverlayGrayBackground />}
          {/* PO Table */}
          <div className={`hidden md:block bg-white transition-all duration-300 w-full overflow-y-auto relative h-full`} ref={wrapperTableRef}>
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
              {access === "edit" && selectedPOs.length > 0 ? (
                <div className="flex items-center gap-2.5 px-4 h-12 flex-wrap bg-gray-900 text-white">
                  <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{selectedPOs.length} selected</span>
                  <span className="text-xs text-white/60 tabular-nums whitespace-nowrap">Owed: ${formatPrice(selectedOwedTotal)}</span>
                  <div className="w-px h-5 bg-white/20" />
                  <button
                    onClick={() => setBulkDialogOpen(true)}
                    className="flex items-center gap-1.5 h-7 px-3 rounded-md bg-white text-gray-900 text-xs font-semibold whitespace-nowrap hover:bg-gray-200 transition-colors"
                  >
                    <CurrencyDollarIcon className="h-3.5 w-3.5" /> Add Payment
                  </button>
                  <button
                    onClick={() => setBulkEditDialogOpen(true)}
                    className="flex items-center gap-1.5 h-7 px-3 rounded-md bg-white text-gray-900 text-xs font-semibold whitespace-nowrap hover:bg-gray-200 transition-colors"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" /> Edit Payment
                  </button>
                  <button
                    onClick={() => setBulkInvoiceDialogOpen(true)}
                    className="flex items-center gap-1.5 h-7 px-3 rounded-md bg-white text-gray-900 text-xs font-semibold whitespace-nowrap hover:bg-gray-200 transition-colors"
                  >
                    <DocumentTextIcon className="h-3.5 w-3.5" /> Invoice #
                  </button>
                  <div className="w-px h-5 bg-white/20" />
                  <button
                    onClick={handleBulkSync}
                    disabled={bulkSyncing}
                    className="flex items-center gap-1.5 h-7 px-3 rounded-md bg-white text-gray-900 text-xs font-semibold whitespace-nowrap hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`h-3.5 w-3.5 ${bulkSyncing ? 'animate-spin' : ''}`} /> Sync
                  </button>
                  <button
                    onClick={() => setSelectedPOIds([])}
                    className="ml-auto p-1 rounded hover:bg-white/10 transition-colors"
                    aria-label="Clear selection"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 px-4 h-12">
                  <div className="relative w-56">
                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search POs..."
                      className="w-full h-8 rounded-md border border-gray-300 bg-white pl-8 pr-7 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:border-blue-500 transition-colors"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                        aria-label="Clear search"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <FinanceStatusFilter
                    options={financeStatusOptions}
                    selectedOptions={filters.status}
                    onChange={(selected) => handleFilterChange('status', selected)}
                  />
                  {filters.status.length > 0 && (
                    <button
                      onClick={() => handleFilterChange('status', [])}
                      className="text-[13px] text-gray-500 hover:text-gray-900 underline underline-offset-2 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                  {pagination.rangeRows && (
                    <span className="ml-auto text-xs text-gray-500 tabular-nums">{pagination.rangeRows}</span>
                  )}
                </div>
              )}
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {access === "edit" && (
                    <th scope="col" className="pl-4 pr-1 py-2.5 w-8">
                      <input
                        type="checkbox"
                        checked={arrayHasItems(orderedPurchases) && orderedPurchases.every((po) => selectedPOIds.includes(po.id))}
                        onChange={toggleSelectAll}
                        aria-label="Select all POs"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </th>
                  )}
                  <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-2/12">PO</th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-1/12">Status</th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-6/12">Payment History</th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-1/12">Total Payments</th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-1/12">Quantity</th>
                  <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-1/12">Costs</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100 relative">
                {!currentFirstLoadComplete && (
                  <div className="z-10 h-60 absolute w-full">
                    <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' testId={testId ? `${testId}_table_loader` : undefined} />
                  </div>
                )}
                {arrayHasItems(orderedPurchases) ? (
                  orderedPurchases.map((po, index) => {
                    const totalPaid = po.amountPaid;
                    const totalOwed = po.amountOwed;
                    const shippingPriceOfThePO =
                      (po.payments || [])
                        .filter((payment) => payment.type === SHIPPING)
                        .reduce((acc, payment) => acc + Number(payment.amount || 0), 0);
                    return (
                      <tr
                        key={`${po.id}_${index}`}
                        className={`cursor-pointer transition-colors ${
                          selectedPO?.id === po.id
                            ? 'bg-[#E8F0FE]'
                            : selectedPOIds.includes(po.id)
                              ? 'bg-gray-50'
                              : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedPO(po)}
                      >
                        {access === "edit" && (
                          <td
                            className="pl-4 pr-1 py-3 w-8"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPOIds.includes(po.id)}
                              onChange={() => toggleSelectPO(po.id)}
                              aria-label={`Select ${po.poNumber}`}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap min-w-[320px]">
                          <div className="flex items-center">
                            <div>
                              <article
                                className="flex items-center gap-2 flex-wrap"
                              >
                                {po.poNumber && (
                                  <div className="text-[16px] font-bold text-gray-900">
                                    {po.poNumber}
                                  </div>
                                )}
                                {access === "edit" && (
                                  <div className='flex gap-2'>
                                    <button
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        if (po.id && !syncingPOs.includes(po.id)) {
                                          dispatch(syncPOItem({
                                            poId: po.id,
                                            shippingPrice: String(shippingPriceOfThePO),
                                            search: searchTerm,
                                            statuses: filters.status,
                                            page: currentPage,
                                            itemsPerPage: currentItemsPerPage
                                          }));
                                        }
                                      }}
                                      className={`sync-button inline-flex gap-1 items-center px-1.5 py-0.5 text-[12px] font-medium rounded transition-colors duration-200
                                        ${syncingPOs.includes(po.id)
                                          ? 'text-white bg-green-500'
                                          : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                                        }`}
                                    >
                                      <span>Sync</span><ArrowPathIcon className={`${syncingPOs.includes(po.id) ? "animate-spin " : ""}h-2.5 w-2.5`} />
                                    </button>
                                    {po.status !== CANCELLED && (
                                      <div className="flex gap-2 items-center">
                                        <button
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            dispatch(syncPOToQB({
                                              poId: po.id,
                                              search: currentSearchValue,
                                              statuses: filters.status,
                                              page: currentPage,
                                              itemsPerPage: currentItemsPerPage
                                            }));
                                          }
                                          }
                                          className={`sync-button inline-flex gap-1 items-center px-1.5 py-0.5 text-[12px] font-medium rounded transition-colors duration-200
                                            ${syncingQBPOs.includes(po.id)
                                              ? 'text-white bg-green-500'
                                              : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                                            }`}
                                        >
                                          <span>Sync QB</span><ArrowPathIcon className={`${syncingQBPOs.includes(po.id) ? "animate-spin " : ""}h-2.5 w-2.5`} />
                                        </button>
                                        <NotificationIcon
                                          po={po}
                                          status={po.qbSyncStatus}
                                          errorText={po.qbSyncError}
                                          clickOnIcon={(errorText) => {
                                            setOpenedNotificationModal({
                                              opened: true,
                                              error: errorText
                                            })
                                          }}
                                        />
                                      </div>
                                    )}
                                    <div className="flex gap-2 items-center">
                                      <InformationCircleIcon className='h-5 w-5 text-blue-600 hover:text-blue-900'
                                        data-tip={true}
                                        data-for={`tooltip-card_${po.id}_${index}`} />
                                      <ReactTooltip
                                        id={`tooltip-card_${po.id}_${index}`}
                                        html={true}
                                        effect='solid'
                                        className='text-right'
                                      >
                                        {financePagePOTooltip(po)}
                                      </ReactTooltip>
                                    </div>
                                  </div>
                                )}
                              </article>
                              <div className="mt-0.5 text-sm text-gray-500">
                                <span className="max-w-48 whitespace-nowrap text-ellipsis overflow-hidden block" title={po.vendor}>{po.vendor}</span>
                              </div>
                              <div className="mt-0.5 text-sm text-gray-500 flex items-center gap-1.5">
                                <CalendarIcon className="h-3.5 w-3.5" />
                                {po.poDate || null}
                                {po.pastDueDays > 0 && pastDueDatesBlockIsVisible(po.status) && (
                                  <span className="px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[12px] font-semibold whitespace-nowrap">
                                    {po.pastDueDays} Days Past Due
                                  </span>
                                )}
                              </div>
                              <div className="mt-0.5 text-sm text-gray-500 flex items-center gap-1.5">
                                <DocumentTextIcon className="h-3.5 w-3.5" />
                                Invoice: {po.invoice || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <PurchaseOrderStatusChip
                            status={po.status}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <MilestoneChips po={po} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">Paid: <span className="font-semibold tabular-nums">${formatPrice(totalPaid)}</span></div>
                          <div className={`text-sm ${getTotalOwedTextColor(totalOwed)}`}>Owed: <span className="font-semibold tabular-nums">${formatPrice(totalOwed)}</span></div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                          {Number(po.quantityAccepted || 0).toLocaleString()} / {Number(po.quantityOrdered || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-500">Shipping: <span className="text-gray-900 tabular-nums">${formatPrice(po.shippingCost)}</span></div>
                          <div className="text-sm text-gray-500">Shipping cost each: <span className="text-gray-900 tabular-nums">${formatPrice(po.shippingCostEach)}</span> each</div>
                          <div className="text-sm text-gray-500">Landed cost: <span className="text-gray-900 tabular-nums">{po.landedCost ? `${po.landedCost}` : "N/A"}</span></div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  currentFirstLoadComplete && (
                    <tr data-testid={testId ? `${testId}_empty_table` : undefined}>
                      <td colSpan={access === "edit" ? "7" : "6"} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                        No purchase orders match the current filters. Try adjusting your search or filter criteria.
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            {currentFirstLoadComplete && (
              <div className="flex items-center justify-end gap-3 px-4 py-3 text-sm text-gray-500">
                <span>Rows per page:</span>
                <select
                  data-testid={testId ? `${testId}_select_pagination` : undefined}
                  value={currentItemsPerPage}
                  onChange={(event) => {
                    setCurrentPage(1);
                    setCurrentItemsPerPage(Number(event.target.value));
                  }}
                  className="h-7 rounded-md border border-gray-300 bg-white px-1.5 text-xs text-gray-900 focus:outline-none focus:border-blue-500"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                {pagination.rangeRows && <span className="tabular-nums">{pagination.rangeRows}</span>}
                <div className="flex gap-1">
                  <button
                    type="button"
                    data-testid={testId ? `${testId}_previous_button` : undefined}
                    disabled={currentPage === 1}
                    onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                    className="h-7 px-2 rounded-md border border-gray-300 bg-white text-xs text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    data-testid={testId ? `${testId}_next_button` : undefined}
                    disabled={pagination.isTheLastPage}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="h-7 px-2 rounded-md border border-gray-300 bg-white text-xs text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Mobile PO cards */}
          <div className="md:hidden bg-white w-full overflow-y-auto relative h-full">
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-3 pt-2 pb-2.5">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search POs..."
                    className="w-full h-9 rounded-md border border-gray-300 bg-white pl-8 pr-2.5 text-[14px] text-gray-900 placeholder-gray-400 outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => setMobileFilterOpen(true)}
                  className={`relative flex items-center justify-center h-9 w-9 rounded-md transition-colors active:scale-[0.97] ${
                    filters.status.length > 0 ? 'bg-gray-900 text-white' : 'border border-gray-300 bg-white text-gray-900'
                  }`}
                  aria-label="Filters"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4" />
                  {filters.status.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                      {filters.status.length}
                    </span>
                  )}
                </button>
              </div>
              <div className="mt-1.5 flex items-center text-[11px] text-gray-500 tabular-nums">
                {pagination.rangeRows || ''}
                {selectedPOIds.length > 0 && <span className="ml-auto">{selectedPOIds.length} selected</span>}
              </div>
            </div>
            {!currentFirstLoadComplete && (
              <div className="h-60 w-full">
                <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' />
              </div>
            )}
            <div className="divide-y divide-gray-100 pb-24">
              {arrayHasItems(orderedPurchases) ? (
                orderedPurchases.map((po, index) => (
                  <MobilePOCard
                    key={`mobile_${po.id}_${index}`}
                    po={po}
                    selectable={access === "edit"}
                    selected={selectedPOIds.includes(po.id)}
                    onToggleSelect={() => toggleSelectPO(po.id)}
                    onOpen={() => setSelectedPO(po)}
                    pastDueVisible={pastDueDatesBlockIsVisible(po.status)}
                  />
                ))
              ) : (
                currentFirstLoadComplete && (
                  <div className="px-6 py-8 text-sm text-gray-500 text-center">
                    No purchase orders match the current filters. Try adjusting your search or filter criteria.
                  </div>
                )
              )}
              {currentFirstLoadComplete && (
                <div className={`flex items-center justify-between px-3 py-3 text-sm text-gray-500 ${selectedPOIds.length > 0 ? 'pb-28' : ''}`}>
                  <span className="tabular-nums text-xs">{pagination.rangeRows}</span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                      className="h-9 px-4 rounded-md border border-gray-300 bg-white text-sm text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      disabled={pagination.isTheLastPage}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="h-9 px-4 rounded-md border border-gray-300 bg-white text-sm text-gray-900 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
            <MobileFilterSheet
              open={mobileFilterOpen}
              onClose={() => setMobileFilterOpen(false)}
              options={financeStatusOptions}
              selectedOptions={filters.status}
              onChange={(selected) => handleFilterChange('status', selected)}
            />
          </div>
          {/* Sidebar */}
          {selectedPO && (
            <POSidebar
              po={selectedPO}
              access={access}
              onClose={() => setSelectedPO(null)}
              searchValue={searchTerm}
              statuses={filters.status}
              poPage={currentPage}
              poItemsPerPage={currentItemsPerPage}
            />
          )}
        </div>
        {access === "edit" && (
          <>
            <BulkBar
              count={selectedPOs.length}
              owedTotal={selectedOwedTotal}
              onAddPayment={() => setBulkDialogOpen(true)}
              onEditPayments={() => setBulkEditDialogOpen(true)}
              onSetInvoices={() => setBulkInvoiceDialogOpen(true)}
              onSync={handleBulkSync}
              syncing={bulkSyncing}
              onClear={() => setSelectedPOIds([])}
            />
            <BulkPaymentDialog
              open={bulkDialogOpen}
              selectedPOs={selectedPOs}
              onSubmit={handleBulkSubmit}
              onClose={() => setBulkDialogOpen(false)}
            />
            <BulkEditPaymentsDialog
              open={bulkEditDialogOpen}
              selectedPOs={selectedPOs}
              onSubmit={handleBulkEditSubmit}
              onClose={() => setBulkEditDialogOpen(false)}
            />
            <BulkInvoiceDialog
              open={bulkInvoiceDialogOpen}
              selectedPOs={selectedPOs}
              onSubmit={handleBulkInvoiceSubmit}
              onClose={() => setBulkInvoiceDialogOpen(false)}
            />
          </>
        )}
        <NotificationModal
          isOpen={openedNotificationModal.opened}
          cancelClick={() => setOpenedNotificationModal({
            opened: false,
            error: null
          })}
          errorText={openedNotificationModal.error}
        />
      </div>
    </main>
  );
}

export default Finance;